import { apply, invert, squareToQuad, type QuadPoints } from './homography';

/**
 * Stem wall (coating carried up the wall base). Two steps:
 *
 * 1. wallContacts: per photo column, the row where the floor meets a wall
 *    (the first floor row of a floor run with wall a short way above it) and
 *    the row where the wall starts. Between them is usually an existing
 *    concrete stem wall, which detection labels neither floor nor wall; it is
 *    coated whole. Columns where the floor meets anything else (cabinets,
 *    cars, doors, the photo edge) get none, so the stem only runs along walls.
 * 2. stemProfile: per column, how many photo pixels one inch of height is
 *    there, from the floor plane, and the stem height: the existing stem wall,
 *    or the configured height where the floor meets the wall directly.
 *    Upright surfaces scale like the floor's least foreshortened direction, so
 *    the stem is taller near the camera and shorter at the back wall.
 */

export interface WallContacts {
  /** Floor row where the floor meets a wall, per column, or -1. */
  contact: Float32Array;
  /** Row where the wall starts above it (top of an existing stem wall), or -1. */
  top: Float32Array;
}

/** Masks are 0..255, row-major, same size. */
export function wallContacts(floor: Uint8Array, wall: Uint8Array, width: number, height: number): WallContacts {
  const contact = new Float32Array(width).fill(-1);
  const top = new Float32Array(width).fill(-1);
  // The unlabeled band between floor and wall: an existing stem wall (3-6% of
  // the photo height near the camera) or just a seam between the two masks.
  const gap = Math.max(3, Math.round(height * 0.1));
  for (let x = 0; x < width; x++) {
    for (let y = 1; y < height; y++) {
      if (floor[y * width + x] <= 127 || floor[(y - 1) * width + x] > 127) continue;
      let wallAt = -1;
      for (let k = 1; k <= gap && y - k >= 0; k++) {
        const i = (y - k) * width + x;
        if (wall[i] > 127) {
          wallAt = y - k + 1;
          break;
        }
        if (floor[i] > 127) break;
      }
      if (wallAt >= 0) {
        contact[x] = y;
        top[x] = wallAt;
        break;
      }
    }
  }
  const close = Math.max(2, height * 0.02);
  const radius = Math.max(1, Math.round(width * 0.006));
  const c = dropShortRuns(smoothContacts(contact, radius, close), close, Math.max(8, Math.round(width * 0.02)));
  return { contact: c, top: medianTops(c, top, radius * 3) };
}

/**
 * Wall start per column from the median stem wall height of nearby columns,
 * so a single column (a corner, a stray mask pixel) cannot stick up.
 */
function medianTops(contact: Float32Array, top: Float32Array, radius: number): Float32Array {
  const out = new Float32Array(contact.length).fill(-1);
  const near: number[] = [];
  for (let x = 0; x < contact.length; x++) {
    if (contact[x] < 0) continue;
    near.length = 0;
    for (let k = Math.max(0, x - radius); k <= Math.min(contact.length - 1, x + radius); k++) {
      if (contact[k] >= 0 && top[k] >= 0) near.push(contact[k] - top[k]);
    }
    if (!near.length) continue;
    near.sort((a, b) => a - b);
    out[x] = contact[x] - near[near.length >> 1];
  }
  return out;
}

/**
 * Removes stretches of contact shorter than `minRun` columns (a run ends where
 * the contact jumps by `close` or more). Real stem walls run along whole walls;
 * short bits come from furniture legs and ragged mask edges.
 */
function dropShortRuns(c: Float32Array, close: number, minRun: number): Float32Array {
  let start = 0;
  for (let x = 1; x <= c.length; x++) {
    const breaks = x === c.length || c[x] < 0 || c[x - 1] < 0 || Math.abs(c[x] - c[x - 1]) >= close;
    if (!breaks) continue;
    if (c[start] >= 0 && x - start < minRun) c.fill(-1, start, x);
    start = x;
  }
  return c;
}

/** Averages each row with nearby columns on the same wall, so a ragged mask edge gives a straight stem. */
function smoothContacts(c: Float32Array, radius: number, close: number): Float32Array {
  const out = new Float32Array(c.length);
  for (let x = 0; x < c.length; x++) {
    if (c[x] < 0) {
      out[x] = -1;
      continue;
    }
    let sum = 0;
    let n = 0;
    for (let k = Math.max(0, x - radius); k <= Math.min(c.length - 1, x + radius); k++) {
      if (c[k] >= 0 && Math.abs(c[k] - c[x]) < close) {
        sum += c[k];
        n++;
      }
    }
    out[x] = sum / n;
  }
  return out;
}

/**
 * Per column, 4 floats: [contact row normalized 0..1 (or -1), photo pixels
 * per inch, stem height in inches, unused]. `widthIn` and `depthIn` are the
 * real size of the quad on the floor; `minHeightIn` is the configured height.
 */
export function stemProfile(
  walls: WallContacts,
  width: number,
  height: number,
  quad: QuadPoints,
  widthIn: number,
  depthIn: number,
  minHeightIn: number,
): Float32Array {
  const { contact, top } = walls;
  const out = new Float32Array(contact.length * 4).fill(-1);
  let toFloor;
  let toImage;
  try {
    toImage = squareToQuad(quad);
    toFloor = invert(toImage);
  } catch {
    return out;
  }
  const px = (u: number, v: number) => {
    const [x, y] = apply(toImage, [u, v]);
    return [x * width, y * height];
  };
  for (let x = 0; x < contact.length; x++) {
    const c = contact[x];
    if (c < 0) continue;
    const ix = (x + 0.5) / width;
    const iy = c / height;
    // Behind the camera or past the horizon: no floor plane there.
    const w = toFloor[6] * ix + toFloor[7] * iy + toFloor[8];
    if (w <= 0) continue;
    const [u, v] = apply(toFloor, [ix, iy]);
    const [x0, y0] = px(u, v);
    const [x1, y1] = px(u + 1 / widthIn, v);
    const [x2, y2] = px(u, v + 1 / depthIn);
    // Largest stretch of the 2x2 Jacobian (pixels per inch on the floor).
    const a = x1 - x0;
    const b = x2 - x0;
    const cc = y1 - y0;
    const d = y2 - y0;
    const s = a * a + b * b + cc * cc + d * d;
    const det = a * d - b * cc;
    const ppi = Math.sqrt((s + Math.sqrt(Math.max(0, s * s - 4 * det * det))) / 2);
    if (!Number.isFinite(ppi) || ppi <= 0) continue;
    out[x * 4] = c / height;
    out[x * 4 + 1] = ppi;
    // Coat the whole existing stem wall; at least the configured height.
    out[x * 4 + 2] = Math.max(minHeightIn, top[x] >= 0 ? (c - top[x]) / ppi : 0);
  }
  return out;
}
